"use client";

import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, LoaderCircle, Send, Sparkles, X } from "lucide-react";
import Markdown from "react-markdown";
import { Trans, useTranslation } from "react-i18next";
import { usePathname } from "next/navigation";

import { AppStateContext } from "@/components/context/AppContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  getCopilotGreeting,
  type CopilotGreeting,
  type CopilotMessage,
} from "@/components/copilot/api";
import { useCopilotThread } from "@/components/copilot/useCopilotThread";
import {
  getNovaDock,
  setNovaOpenRequestHandler,
  subscribeNovaDock,
} from "@/components/copilot/dock";
import { NovaSpotlight } from "@/components/copilot/NovaSpotlight";
import { useAnalytics } from "@/components/analytics/AnalyticsProvider";
import { EVENTS } from "@/lib/analytics/events";
import { useFirstRunState } from "@/lib/firstRun";
import { msg, t as translate } from "@/lib/i18n";
import ScheduleInterviewFilm from "@/components/copilot/ScheduleInterviewFilm";

const GREETING_DISMISSED_KEY = "aiia-nova-greeting-dismissed";

// One whole sentence each, the hint inside <0>: word order differs by language.
const QUIET_HINTS = [
  msg("Ask Nova: <0>how many candidates are waiting on me?</0>"),
  msg("Ask Nova: <0>set up my career page</0>"),
  msg("Ask Nova: <0>where are the resumes I uploaded?</0>"),
  msg("Ask Nova: <0>write interview questions for a role</0>"),
  msg("Ask Nova: <0>which candidates scored best?</0>"),
  msg("Ask Nova: <0>publish my role to the career page</0>"),
] as const;

// Proposals that change a call (not send something): saved, not sent.
const CALL_PROPOSAL_TYPES = ["update_call_submittal", "apply_call_fields"];
const CALL_PATH = /^\/dashboard\/agency\/calls\/[^/]+\/?$/;
// A time Nova cites, "[0:42]" or "[1:02:05]". The backend already dropped any outside the call.
const CITATION = /\[(\d{1,2}(?::\d{2}){1,2})\](?!\()/g;
const CITATION_HREF = "#t=";

function citationSeconds(time: string) {
  return time.split(":").reduce((total, part) => total * 60 + Number(part), 0);
}

// Citations become links with a special href; the link renderer turns them into buttons.
function withCitationLinks(text: string) {
  return text.replace(CITATION, (_match, time: string) => `[${time}](${CITATION_HREF}${citationSeconds(time)})`);
}

const CITATION_COMPONENTS = {
  a({ href, children }: { href?: string; children?: ReactNode }) {
    if (href?.startsWith(CITATION_HREF)) {
      const seconds = Number(href.slice(CITATION_HREF.length));
      return (
        <button
          className="mx-0.5 rounded-md border border-border bg-white px-1.5 text-xs font-medium tabular-nums hover:bg-[--rx-soft]"
          onClick={() =>
            window.dispatchEvent(new CustomEvent("aiia:call-seek", { detail: { seconds } }))
          }
          type="button"
        >
          {children}
        </button>
      );
    }
    // Outside links open in a new tab; a portal link stays in this one.
    return /^https?:\/\//.test(href || "") ? (
      <a href={href} rel="noopener noreferrer" target="_blank">
        {children}
      </a>
    ) : (
      <a href={href}>{children}</a>
    );
  },
};

function pathOnly(path: string) {
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

// Only the recruiter's own messages carry the page they were sent from, so a reply
// belongs to the page of the message it answers.
function messagesOnPage(messages: CopilotMessage[], pathname: string) {
  const here = pathOnly(pathname);
  let page = "";
  return messages.filter((message) => {
    if (message.page) page = pathOnly(message.page);
    return page === here;
  });
}

function proposalStatusText(status: CopilotMessage["proposal_status"], t: (text: string) => string) {
  if (status === "confirmed") return t("Confirmed and sent.");
  if (status === "declined") return t("Cancelled.");
  if (status === "expired") return t("This proposal expired.");
  return "";
}

function Message({
  message,
  proposalId,
  onProposal,
  citations,
  copyable,
}: {
  message: CopilotMessage;
  proposalId: number | string | null;
  onProposal: (messageId: number | string, action: "confirm" | "decline") => void;
  citations: boolean;
  copyable: boolean;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const stale = message.run_status === "running" && message.stale;
  const failed = stale || message.run_status === "error";
  const isProposalBusy = proposalId === message.id;
  const proposalSummary = message.proposal?.summary || message.content;
  const savesCall = CALL_PROPOSAL_TYPES.includes(String(message.proposal?.type));
  const copy = () => {
    navigator.clipboard
      .writeText(message.content)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <div
      className={
        message.role === "user"
          ? "ml-10 rounded-[10px] bg-[--rx-soft] px-3 py-2 text-sm"
          : "mr-10 text-sm"
      }
    >
      {message.role === "assistant" && (
        <p className="mb-1 text-xs font-medium text-muted-foreground">Nova</p>
      )}
      {message.content && message.proposal_status !== "pending" && (
        <div className="leading-6 [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:mb-2 [&_ul]:space-y-1">
          {citations ? (
            <Markdown components={CITATION_COMPONENTS} disallowedElements={["img"]} unwrapDisallowed>
              {withCitationLinks(message.content)}
            </Markdown>
          ) : (
            // No images: a reply shaped by outside text (a CV, a call) must not load a remote URL.
            <Markdown components={CITATION_COMPONENTS} disallowedElements={["img"]} unwrapDisallowed>
              {message.content}
            </Markdown>
          )}
        </div>
      )}

      {copyable &&
        message.role === "assistant" &&
        message.content &&
        message.proposal_status !== "pending" && (
          <button
            className="mt-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={copy}
            type="button"
          >
            {copied ? t("Copied") : t("Copy")}
          </button>
        )}

      {message.steps.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {message.steps.map((step, index) => (
            <div
              className="flex items-start gap-2 text-xs text-muted-foreground"
              key={`${message.id}-${step.tool || "step"}-${index}`}
            >
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[--rx-ink]" />
              <span>{step.label || step.tool || t("Step complete")}</span>
            </div>
          ))}
        </div>
      )}

      {message.run_status === "running" && !stale && (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
          {t("Working…")}
        </div>
      )}

      {failed && (
        <p className="mt-2 whitespace-pre-wrap text-sm">
          {stale ? t("This reply stopped before it finished.") : message.error || t("This reply failed.")}
        </p>
      )}

      {message.proposal_status === "pending" && (
        <div className="mt-2 rounded-xl border border-border bg-white p-3">
          <p className="font-medium leading-6">{proposalSummary}</p>
          {savesCall && message.proposal.preview ? (
            <p className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
              {message.proposal.preview}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              className="h-9 px-3"
              disabled={isProposalBusy}
              onClick={() => onProposal(message.id, "confirm")}
              type="button"
            >
              {isProposalBusy && <LoaderCircle className="animate-spin" />}
              {savesCall ? t("Confirm and save") : t("Confirm and send")}
            </Button>
            <Button
              className="h-9 px-3"
              disabled={isProposalBusy}
              onClick={() => onProposal(message.id, "decline")}
              type="button"
              variant="outline"
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      )}

      {message.proposal_status && message.proposal_status !== "pending" && (
        <p className="mt-2 text-xs text-muted-foreground">
          {savesCall && message.proposal_status === "confirmed" ? t("Saved") : proposalStatusText(message.proposal_status, t)}
        </p>
      )}
    </div>
  );
}

export function CopilotBar() {
  const { t, i18n } = useTranslation();
  const { userProfile } = useContext(AppStateContext);
  const isRecruiter = userProfile?.role === "hr";
  const pathname = usePathname();
  // A record page docks Nova beside the record; otherwise the dropdown.
  const dock = useSyncExternalStore(subscribeNovaDock, getNovaDock, () => null);
  const docked = Boolean(dock);
  const isCallPage = CALL_PATH.test(pathname || "");
  const firstRunState = useFirstRunState();
  const { track } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [greeting, setGreeting] = useState<CopilotGreeting | null>(null);
  const [greetingDismissed, setGreetingDismissed] = useState<boolean | null>(
    null
  );
  const [greetingEnded, setGreetingEnded] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  const [spotlightNotice, setSpotlightNotice] = useState("");
  const [hintIndex, setHintIndex] = useState(() =>
    Math.floor(Math.random() * QUIET_HINTS.length)
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const openForArrival = useCallback(() => setIsOpen(true), []);
  const {
    thread,
    isLoading,
    isSending,
    proposalId,
    notice,
    hasRunningMessage,
    sendMessage,
    respondToProposal,
    activeDirective,
    dismissDirective,
  } = useCopilotThread(isRecruiter, pathname || "", openForArrival);

  const markSpotlightUnavailable = useCallback(() => {
    setSpotlightNotice(
      translate("I couldn’t highlight that here because it isn’t on this page.")
    );
  }, []);
  useEffect(() => {
    if (activeDirective) setSpotlightNotice("");
  }, [activeDirective]);

  const messages = thread?.messages || [];
  const conversationMessages = messages.filter(
    (message) => message.role !== "system"
  );
  const latestAssistant = [...messages]
    .reverse()
    .find((message) => message.role === "assistant");

  // The bar carries a message only while something is genuinely unhandled:
  // a proposal waiting on a decision, or a reply that arrived since the panel
  // was last open. Otherwise it goes quiet. Showing the newest reply whenever
  // one exists parks a stale answer at the top of every page forever.
  const [seenMessageId, setSeenMessageId] = useState<string | number | null>(
    null
  );
  const historyMarked = useRef(false);
  useEffect(() => {
    // Whatever was already in the thread when the page loaded counts as read.
    // Only a reply that arrives while you are here should light the bar up.
    if (!historyMarked.current && thread) {
      historyMarked.current = true;
      setSeenMessageId(latestAssistant?.id ?? null);
    }
  }, [thread, latestAssistant?.id]);
  useEffect(() => {
    if (isOpen && latestAssistant) setSeenMessageId(latestAssistant.id);
  }, [isOpen, latestAssistant?.id]);

  const awaitingDecision = latestAssistant?.proposal_status === "pending";
  const hasPendingProposal = messages.some(
    (message) => message.proposal_status === "pending"
  );
  const unread =
    Boolean(latestAssistant) && latestAssistant!.id !== seenMessageId;
  const hasSomething = awaitingDecision || unread || Boolean(spotlightNotice);
  const isDashboard = pathname === "/dashboard/agency";
  // The greeting opens the panel by itself, so it waits until the dashboard
  // home knows it is not showing the first-run wizard.
  const showGreeting = Boolean(
    isDashboard &&
      firstRunState === "not-first-run" &&
      greeting &&
      greetingDismissed === false &&
      !greetingEnded &&
      !isLoading &&
      thread &&
      !hasPendingProposal &&
      !unread &&
      !hasRunningMessage &&
      !spotlightNotice
  );

  const latestUserIndex = conversationMessages.reduce(
    (latest, message, index) => (message.role === "user" ? index : latest),
    -1
  );
  const latestExchangeStart =
    latestUserIndex >= 0
      ? latestUserIndex
      : Math.max(conversationMessages.length - 1, 0);
  // Docked, the panel shows this page's messages; the rest count as earlier.
  const pageMessages = docked ? messagesOnPage(conversationMessages, pathname || "") : [];
  // The latest exchange on this call only; the rest stays behind "Show earlier".
  const pageLatestUser = pageMessages.map((message) => message.role).lastIndexOf("user");
  const pageExchange = pageMessages.slice(Math.max(pageLatestUser, 0));
  const earlierCount = docked
    ? conversationMessages.length - pageExchange.length
    : showGreeting
      ? conversationMessages.length
      : latestExchangeStart;
  const visibleMessages = showEarlier
    ? conversationMessages
    : docked
      ? pageExchange
      : showGreeting
        ? []
        : conversationMessages.slice(latestExchangeStart);
  const showStarters = docked && !isLoading && pageMessages.length === 0;
  const callStarters = [
    {
      id: "pay",
      label: t("Pay, notice and location"),
      prompt: t("What did they say about pay, notice and location on this call?"),
    },
    {
      id: "submittal",
      label: t("Draft the submittal"),
      prompt: t("Draft the submittal for my client from this call."),
    },
    {
      id: "profile",
      label: t("Fill the candidate profile"),
      prompt: t("Fill the candidate profile from this call."),
    },
    {
      id: "email",
      label: t("Follow-up email"),
      prompt: t("Write a follow-up email to the candidate from this call."),
    },
  ];

  useEffect(() => {
    if (!isRecruiter) return;
    setGreetingDismissed(
      window.sessionStorage.getItem(GREETING_DISMISSED_KEY) === "1"
    );
  }, [isRecruiter]);

  // The server writes the greeting in the portal language it is sent, so it is
  // asked again when that changes: the switcher, or the account's language
  // arriving after the first paint on a new browser.
  useEffect(() => {
    if (!isRecruiter || !isDashboard) {
      setGreeting(null);
      return;
    }
    let cancelled = false;
    void getCopilotGreeting()
      .then((nextGreeting) => {
        if (!cancelled) setGreeting(nextGreeting);
      })
      .catch(() => {
        // The rotating quiet hints are the silent fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [isDashboard, isRecruiter, i18n.language]);

  useEffect(() => {
    if (!showGreeting) return;
    setShowEarlier(false);
    setIsOpen(true);
  }, [showGreeting]);

  useEffect(() => {
    if (!isOpen) setShowEarlier(false);
  }, [isOpen]);

  const dismissGreeting = useCallback(() => {
    window.sessionStorage.setItem(GREETING_DISMISSED_KEY, "1");
    setGreetingDismissed(true);
    setShowEarlier(false);
    setIsOpen(false);
  }, []);

  const closePanel = useCallback(() => {
    if (showGreeting) {
      dismissGreeting();
      return;
    }
    setShowEarlier(false);
    setIsOpen(false);
  }, [dismissGreeting, showGreeting]);

  useEffect(() => {
    if (isOpen || hasRunningMessage || hasSomething) return;
    const interval = window.setInterval(
      () => setHintIndex((current) => (current + 1) % QUIET_HINTS.length),
      5_000
    );
    return () => window.clearInterval(interval);
  }, [hasRunningMessage, hasSomething, isOpen]);

  useEffect(() => {
    if (!isRecruiter) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setIsOpen(true);
      } else if (event.key === "Escape") {
        closePanel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closePanel, isRecruiter]);

  // The page learns when Nova opens or closes (it shows the Ask Nova tab) and can ask for either.
  useEffect(() => {
    dock?.onOpenChange(isOpen);
  }, [dock, isOpen]);
  useEffect(
    () => setNovaOpenRequestHandler((open) => (open ? setIsOpen(true) : closePanel())),
    [closePanel]
  );
  // Leaving the call page takes the dock away: close Nova rather than reopen it as a dropdown.
  const hadDock = useRef(false);
  useEffect(() => {
    if (hadDock.current && !dock && isOpen) closePanel();
    hadDock.current = Boolean(dock);
  }, [closePanel, dock, isOpen]);
  // On a phone the dock is above the record: bring it into view once its tab shows.
  useEffect(() => {
    if (!isOpen || !dock || !window.matchMedia("(max-width: 1023px)").matches) return;
    const frame = window.requestAnimationFrame(() =>
      dock.element.scrollIntoView({ block: "start", behavior: "smooth" })
    );
    return () => window.cancelAnimationFrame(frame);
  }, [dock, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Docked, clicking the record (a transcript line) must not close Nova.
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closePanel();
    };
    if (docked) {
      // The dock's tab is still hidden in the render that opens it.
      const frame = window.requestAnimationFrame(() =>
        composerRef.current?.focus({ preventScroll: true })
      );
      return () => window.cancelAnimationFrame(frame);
    }
    document.addEventListener("mousedown", handlePointerDown);
    composerRef.current?.focus();
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [closePanel, docked, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (docked) {
      // Scroll the list, not the page: a reply arriving must not jump a phone to the dock.
      const list = listRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    } else {
      endRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [docked, isOpen, messages]);

  if (!isRecruiter) return null;

  const submit = async () => {
    const sent = await sendMessage(draft, pathname || "");
    if (sent) setDraft("");
  };

  const handleProposal = async (
    message: CopilotMessage,
    action: "confirm" | "decline"
  ) => {
    const ok = await respondToProposal(message.id, action);
    if (ok && action === "confirm" && CALL_PROPOSAL_TYPES.includes(String(message.proposal.type))) {
      window.dispatchEvent(
        new CustomEvent("aiia:call-changed", { detail: { callId: message.proposal.call_id } })
      );
    }
  };

  const submitGreetingOffer = async (prompt: string) => {
    setGreetingEnded(true);
    await sendMessage(prompt, pathname || "");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  const panelBody = (
    <div
      className={`flex flex-col ${
        docked
          ? "h-[min(70vh,32rem)] lg:h-[calc(100vh-140px)]"
          : "max-h-[min(62vh,560px)]"
      }`}
    >
      {!docked && (
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold">
            {showGreeting ? "Nova" : t("Ask Nova")}
          </h2>
        </div>
      )}

      <div
        className="min-h-40 flex-1 space-y-4 overflow-y-auto px-4 py-4"
        ref={listRef}
      >
        {earlierCount > 0 && (
          <button
            aria-expanded={showEarlier}
            className="flex w-full items-center gap-1.5 text-left text-xs text-muted-foreground"
            onClick={() => setShowEarlier((current) => !current)}
            type="button"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 ${showEarlier ? "rotate-180" : ""}`}
            />
            {showEarlier
              ? t("Hide earlier")
              : t("Show earlier ({{n}})", { n: earlierCount })}
          </button>
        )}

        {isLoading && conversationMessages.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            {t("Loading conversation…")}
          </div>
        ) : showStarters && !showEarlier ? (
          <div>
            <p className="text-sm text-muted-foreground">
              {t("Ask anything about this call, or ask Nova to do something with it.")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {callStarters.map((starter) => (
                <button
                  className="rounded-[9px] border border-border bg-white px-3 py-1.5 text-left text-xs font-medium hover:bg-[--rx-soft]"
                  disabled={isSending}
                  key={starter.label}
                  onClick={() => {
                    track(EVENTS.NOVA_CALL_STARTER, { starter: starter.id, call_id: Number(pathname?.split("/")[4]) || null });
                    void sendMessage(starter.prompt, pathname || "");
                  }}
                  type="button"
                >
                  {starter.label}
                </button>
              ))}
            </div>
          </div>
        ) : conversationMessages.length === 0 && !showGreeting ? (
          <p className="text-sm text-muted-foreground">
            {t("Ask about candidates, roles, interviews, or anything in your account.")}
          </p>
        ) : (
          visibleMessages.map((message) => (
            <Message
              key={message.id}
              message={message}
              citations={isCallPage}
              copyable={docked}
              onProposal={(_messageId, action) => void handleProposal(message, action)}
              proposalId={proposalId}
            />
          ))
        )}

        {showGreeting && greeting && (
          <div className="mr-10 text-sm">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Nova
            </p>
            <p className="leading-6">{greeting.greeting}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {greeting.offers.map((offer) => (
                <button
                  className="rounded-[9px] border border-border bg-white px-3 py-1.5 text-left text-xs font-medium hover:bg-[--rx-soft]"
                  disabled={isSending}
                  key={`${offer.label}:${offer.prompt}`}
                  onClick={() => void submitGreetingOffer(offer.prompt)}
                  type="button"
                >
                  {offer.label}
                </button>
              ))}
            </div>
            {greeting.film === "schedule-interview" && (
              <div className="mt-4 max-w-sm">
                <p className="mb-2 text-xs text-muted-foreground">
                  {t("New here? Watch how an interview gets scheduled, start to finish.")}
                </p>
                <ScheduleInterviewFilm />
              </div>
            )}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {(notice || spotlightNotice) && (
        <p className="border-t border-border px-4 py-2 text-sm">
          {notice || spotlightNotice}
        </p>
      )}

      <form
        className="flex items-end gap-2 border-t border-border p-3"
        onSubmit={handleSubmit}
      >
        <Textarea
          aria-label={t("Message Nova")}
          className="rx-field min-h-9 resize-none !py-[7px]"
          disabled={isSending}
          onChange={(event) => {
            if (showGreeting) setGreetingEnded(true);
            setDraft(event.target.value);
          }}
          onKeyDown={handleComposerKeyDown}
          placeholder={docked ? t("Ask about this call") : t("Ask Nova anything")}
          ref={composerRef}
          rows={1}
          value={draft}
        />
        <Button
          aria-label={t("Send message")}
          className="h-9 w-9 shrink-0 p-0"
          disabled={!draft.trim() || isSending}
          type="submit"
        >
          {isSending ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <Send />
          )}
        </Button>
      </form>
    </div>
  );

  return (
    <>
    <div className="relative z-40 px-4 pt-4" ref={rootRef}>
      <div className="relative mx-auto max-w-3xl">
        <div
          className={`flex h-10 w-full items-center rounded-[10px] border bg-white px-1 ${
            showGreeting ? "transition-none" : "transition-[border-color,box-shadow]"
          } ${
            isOpen
              ? "border-[--rx-blue] shadow-[0_0_0_3px_#266df01f]"
              : hasSomething
                ? "border-input"
                : "border-input text-muted-foreground"
          }`}
        >
          <button
            aria-label={isOpen ? t("Close Nova") : t("Open Nova")}
            aria-expanded={isOpen}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-3 text-left"
            onClick={() => {
              if (isOpen) closePanel();
              else setIsOpen(true);
            }}
            type="button"
          >
            <Sparkles className="h-4 w-4 shrink-0 text-[--rx-ink]" />
            <span className="min-w-0 flex-1 truncate text-sm">
              {hasRunningMessage
                ? t("Nova is working…")
                : spotlightNotice
                  ? spotlightNotice
                : hasSomething
                  ? latestAssistant?.content
                  : (
                    <span className="text-muted-foreground">
                      <Trans
                        i18nKey={QUIET_HINTS[hintIndex]}
                        components={[<span key="hint" className="text-foreground" />]}
                      />
                    </span>
                  )}
            </span>
            <kbd className="hidden rounded-[5px] border border-input px-1.5 py-0.5 text-[11px] text-muted-foreground sm:inline">
              ⌘K
            </kbd>
            {isOpen ? (
              <X className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0" />
            )}
          </button>
          {latestAssistant?.proposal_status === "pending" && !isOpen && (
            <button
              className="mr-1 rounded-lg bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
              onClick={() => setIsOpen(true)}
              type="button"
            >
              {t("Review", { context: "action" })}
            </button>
          )}
        </div>

        {isOpen &&
          (dock ? (
            createPortal(
              <section
                aria-label={t("Nova copilot")}
                className="overflow-hidden rounded-2xl border border-border bg-white"
              >
                {panelBody}
              </section>,
              dock.element
            )
          ) : (
            <section
              aria-label={t("Nova copilot")}
              className="absolute left-0 right-0 top-12 overflow-hidden rounded-2xl border border-border bg-white shadow-[0_1px_2px_#1c1d1f0a,0_16px_40px_-12px_#1c1d1f29]"
            >
              {panelBody}
            </section>
          ))}
      </div>
    </div>
    <NovaSpotlight
      directive={activeDirective}
      onDismiss={dismissDirective}
      onUnavailable={markSpotlightUnavailable}
    />
    </>
  );
}
